import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Input, FormControl, FormLabel, Textarea, Select,
  useToast, Image, Box, Text,
} from '@chakra-ui/react';
import { FiCamera } from 'react-icons/fi';
import api from '../api';
import ServiceGroupPicker from './ServiceGroupPicker';

const COMPANY_TYPES = [
  { value: 'customer', label: 'Customer' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'group_brand', label: 'Group Brand or Company' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'joint_venture', label: 'Joint Venture' },
];

export default function CompanyModal({ isOpen, onClose, initialName, onCreated }) {
  const [form, setForm] = useState({
    name: '', website: '', phone: '', address: '',
    company_number: '', vat_number: '', lead_source: '', company_type: 'customer', notes: '',
  });
  const [serviceGroups, setServiceGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: initialName || '', website: '', phone: '', address: '',
        company_number: '', vat_number: '', lead_source: '', company_type: 'customer', notes: '',
      });
      setServiceGroups([]);
      setLogoFile(null); setLogoPreview(null);
    }
  }, [isOpen, initialName]);

  const handleChange = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Company name is required', status: 'warning', duration: 2000 });
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/companies', form);
      // Upload logo if selected
      if (logoFile && data.id) {
        try {
          const formData = new FormData();
          formData.append('file', logoFile);
          formData.append('folder', 'logos');
          formData.append('entity_type', 'company');
          formData.append('entity_id', String(data.id));
          const { data: fileData } = await api.post('/files/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          await api.put(`/companies/${data.id}`, { logo_file_id: fileData.id });
        } catch(e) { console.error('Logo upload failed:', e); }
      }
      toast({ title: `"${data.name}" created`, status: 'success', duration: 2000 });
      // Save service groups
      if (serviceGroups.length > 0 && data.id) {
        try {
          await api.put(`/service-groups/for/company/${data.id}`, { service_group_ids: serviceGroups.map(s => s.id) });
        } catch(e) {}
      }
      onCreated(data);
    } catch (err) {
      toast({ title: 'Failed to create company', status: 'error', duration: 3000 });
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside">
      <ModalOverlay />
      {/* Geometry contract: all four edges pinned 10% in from the screen.
          The modal's size is DEFINED by its margins (same pattern as the
          Communications modal fix) — it can never run off the bottom
          regardless of form length; the body scrolls inside instead.
          w/maxW auto+none override Chakra's size-md width so the insets
          win, and my={0} kills the default vertical margins that would
          otherwise add to the 10% offsets. */}
      <ModalContent position="fixed" top="10%" bottom="10%" left="10%" right="10%"
        w="auto" maxW="none" h="auto" maxH="none" my={0}
        display="flex" flexDirection="column" overflow="hidden">
        <ModalHeader fontSize="md">New Company</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4} flex={1} overflowY="auto">
          {/* The pinned box spans 80% of the screen; on wide monitors the
              form itself is capped and centred so fields stay readable —
              the 10% margins are untouched. */}
          <Box maxW="820px" mx="auto" w="100%">
          <VStack spacing={3}>
            <HStack w="100%" spacing={3} align="flex-start">
              <Box flexShrink={0}>
                <input type="file" ref={fileRef} style={{ display: 'none' }} accept="image/*" onChange={handleLogoSelect} />
                <Box w="60px" h="60px" border="2px dashed" borderColor={logoPreview ? 'transparent' : 'gray.200'}
                  borderRadius="md" overflow="hidden" cursor="pointer" onClick={() => fileRef.current?.click()}
                  display="flex" alignItems="center" justifyContent="center" bg="gray.50">
                  {logoPreview ? (
                    <Image src={logoPreview} w="100%" h="100%" objectFit="cover" />
                  ) : (
                    <VStack spacing={0}><FiCamera size={16} color="gray" /><Text fontSize="8px" color="gray.400">Logo</Text></VStack>
                  )}
                </Box>
              </Box>
              <FormControl flex={1}>
                <FormLabel fontSize="xs" mb={1}>Company Name *</FormLabel>
                <Input size="sm" value={form.name} onChange={handleChange('name')} autoFocus />
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Website</FormLabel>
              <Input size="sm" value={form.website} onChange={handleChange('website')} placeholder="https://" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Main Phone</FormLabel>
              <Input size="sm" value={form.phone} onChange={handleChange('phone')} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Physical Address</FormLabel>
              <Textarea size="sm" value={form.address} onChange={handleChange('address')} rows={2} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Notes <Text as="span" color="gray.400" fontWeight="400">(relationship, what they sell/buy, etc.)</Text></FormLabel>
              <Textarea size="sm" value={form.notes} onChange={handleChange('notes')} rows={3} placeholder="Summary of our relationship, what they supply, key context…" />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Company Number</FormLabel>
              <Input size="sm" value={form.company_number} onChange={handleChange('company_number')} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>VAT Number</FormLabel>
              <Input size="sm" value={form.vat_number} onChange={handleChange('vat_number')} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Company Type</FormLabel>
              <Select size="sm" value={form.company_type} onChange={handleChange('company_type')}>
                {COMPANY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Lead Source</FormLabel>
              <Input size="sm" value={form.lead_source} onChange={handleChange('lead_source')} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Services &amp; Product Groups</FormLabel>
              <ServiceGroupPicker selected={serviceGroups} onChange={setServiceGroups} />
            </FormControl>
          </VStack>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
            onClick={handleSave} isLoading={saving}>
            Create Company
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
